import React from "react";

type MetricCardProps = {
  title: string;
  value: string;
  accent?: "blue" | "green" | "amber" | "violet" | "rose";
};

const accentStyles: Record<NonNullable<MetricCardProps["accent"]>, string> = {
  blue: "from-blue-500 to-cyan-500",
  green: "from-emerald-500 to-teal-500",
  amber: "from-amber-500 to-orange-500",
  violet: "from-violet-500 to-indigo-500",
  rose: "from-rose-500 to-pink-500",
};

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  accent = "blue",
}) => (
  <div className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
    <div
      className={`h-1.5 w-16 rounded-full bg-gradient-to-r ${accentStyles[accent]} mb-3`}
    />
    <p className="text-xs uppercase tracking-wide text-slate-500">{title}</p>
    <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
  </div>
);

type PanelProps = {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
};

export const Panel: React.FC<PanelProps> = ({ title, subtitle, children }) => (
  <section className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {subtitle ? <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p> : null}
    </div>
    {children}
  </section>
);

type StatusBadgeProps = {
  label: string;
  tone?: "default" | "info" | "success" | "warning" | "danger";
};

const badgeTone: Record<NonNullable<StatusBadgeProps["tone"]>, string> = {
  default: "bg-slate-100 text-slate-700",
  info: "bg-blue-100 text-blue-700",
  success: "bg-emerald-100 text-emerald-700",
  warning: "bg-amber-100 text-amber-700",
  danger: "bg-rose-100 text-rose-700",
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ label, tone = "default" }) => (
  <span className={`px-2 py-1 text-xs rounded-full font-medium ${badgeTone[tone]}`}>
    {label}
  </span>
);

type MiniBarChartProps = {
  values: number[];
};

export const MiniBarChart: React.FC<MiniBarChartProps> = ({ values }) => (
  <div className="h-28 flex items-end gap-2">
    {values.map((value, index) => (
      <div key={`${value}-${index}`} className="flex-1 bg-slate-100 rounded-md overflow-hidden">
        <div
          className="w-full bg-gradient-to-t from-indigo-500 to-violet-500 rounded-md"
          style={{ height: `${Math.max(8, Math.min(value, 100))}%` }}
        />
      </div>
    ))}
  </div>
);
